import React, { useEffect, useMemo, useState, useRef } from 'react';
import { trimStart, invert } from 'lodash-es';

interface IAnchorContext {
  setAnchor(anchor: string): void;
  anchor?: string;
  anchorTop: { [anchor: string]: number };
  defaultAnchor?: string;
  setAnchorTop(anchor: string, top: number): void;
  getAnchorTop(anchor: string): number;
  rafRef?: any;
}

const AnchorContext = React.createContext<IAnchorContext>({
  setAnchor: () => {},
  anchor: '',
  anchorTop: {},
  defaultAnchor: '',
  setAnchorTop: () => {},
  getAnchorTop: () => 0,
});

interface IProps {
  defaultAnchor?: string;
  scrollTop: number;
  children?: React.ReactNode;
}

function AnchorProvider(props: IProps) {
  const { defaultAnchor, scrollTop, children } = props;
  const [anchor, setAnchor] = useState<string>(defaultAnchor || '');
  const [anchorTop, setAnchorTop] = useState<{ [anchor: string]: number }>({});
  const rafRef = useRef<{ manual: boolean; id: number }>({ manual: false, id: 0 });
  const anchorTopList = useMemo(() => {
    return [
      ...Object.keys(anchorTop)
        .map((key) => {
          return anchorTop[key];
        })
        .sort((a, b) => a - b),
      +Infinity,
    ];
  }, [anchorTop]);
  const topToAnchor = useMemo(() => {
    return invert(anchorTop);
  }, [anchorTop]);

  useEffect(() => {
    if (rafRef.current.manual) return;
    if (scrollTop <= anchorTopList[0]) {
      setAnchor(topToAnchor[anchorTopList[0]]);
      return;
    }

    for (let i = 0; i < anchorTopList.length; i++) {
      if (scrollTop >= anchorTopList[i] && scrollTop < anchorTopList[i + 1]) {
        setAnchor(topToAnchor[anchorTopList[i]]);
        break;
      }
    }
  }, [anchorTopList, topToAnchor, scrollTop]);

  useEffect(() => {
    const hash = trimStart(window.location.hash, '#');
    if (hash) {
      setAnchor(hash);
    }
  }, []);

  function nextTick(callback: any) {
    const steps = () => {
      const res = callback();
      if (!res) {
        rafRef.current.id = requestAnimationFrame(steps);
      }
    };
    rafRef.current.id = requestAnimationFrame(steps);
  }

  const contextVal = useMemo(() => {
    return {
      defaultAnchor,
      anchor,
      anchorTop,
      rafRef,
      setAnchor: (a: string) => {
        // 人为移动
        rafRef.current.manual = true;
        setAnchor(a);
        const newScrollTop = anchorTop[a];
        window.scrollTo({
          top: newScrollTop,
        });
        // 清空上一次的请求
        cancelAnimationFrame(rafRef.current.id);
        nextTick(() => {
          if (window.scrollY === newScrollTop) {
            rafRef.current.manual = false;
            return true;
          }
          return false;
        });
      },
      getAnchorTop: (_anchor: string) => {
        return anchorTop[_anchor];
      },
      setAnchorTop: (_anchor: string, _top: number) => {
        setAnchorTop((prev) => {
          return {
            ...prev,
            [_anchor]: _top,
          };
        });
      },
    };
  }, [defaultAnchor, anchor, anchorTop, setAnchor]);

  return <AnchorContext.Provider value={contextVal}>{children}</AnchorContext.Provider>;
}

function useNavAnchor() {
  const context = React.useContext(AnchorContext);
  return context;
}

export { AnchorProvider, AnchorContext, useNavAnchor };
